<template>
	<div class="language-switcher" @click.prevent="toggleLanguage">
		<div class="language-pill" :class="{ switching: isSwitching }">
			<!-- Globe Icon -->
			<div class="globe-icon">
				<svg viewBox="0 0 24 24" version="1.1">
					<g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
						<path d="M11.7797867,3.50630764 C14.8975452,5.88345485 16.5,8.81577528 16.5,12 C16.5,15.2364043 14.8446007,18.2125843 12.0850842,20.6127131 C9.10282039,18.1172115 7.5,15.1845869 7.5,12 C7.5,8.81678659 9.10143178,5.88531557 11.7797867,3.50630764 Z" id="intersection" stroke="currentColor"></path>
						<circle id="circle" stroke="currentColor" cx="12" cy="12" r="8.5"></circle>
						<line x1="12" y1="4" x2="12" y2="20" id="vertical-center" stroke="currentColor"></line>
						<line x1="4" y1="15" x2="20" y2="15" id="bottom-line" stroke="currentColor"></line>
						<line x1="4" y1="9" x2="20" y2="9" id="top-line" stroke="currentColor"></line>
					</g>
				</svg>
			</div>

			<!-- Active Language -->
			<div class="lang-text active">
				{{ currentLang.toUpperCase() }}
			</div>

			<!-- Divider -->
			<div class="lang-divider"></div>

			<!-- Inactive Language -->
			<div class="lang-text inactive">
				{{ inactiveLang.toUpperCase() }}
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { loadMessages } from '~/plugins/i18n'

export default {
	data() {
		return {
			isSwitching: false
		}
	},

	computed: {
		...mapGetters({
			locale: 'lang/locale',
			locales: 'lang/locales'
		}),
		currentLang() {
			return this.$i18n.locale || this.locale || 'ar';
		},
		inactiveLang() {
			return this.currentLang === 'ar' ? 'en' : 'ar';
		}
	},

	methods: {
		async toggleLanguage() {
			// Get the inactive language (the one we're switching to)
			const newLocale = this.inactiveLang;

			if (this.$i18n.locale !== newLocale) {
				// Add switching animation class
				this.isSwitching = true;

				// Optional GA tracking
				if (this.$ga && this.$ga.event) {
					this.$ga.event('Language', 'Changed', newLocale, 1);
				}

				// Load messages and set locale
				await loadMessages(newLocale);
				this.$store.dispatch('lang/setLocale', { locale: newLocale });

				// Remove switching animation after transition
				setTimeout(() => {
					this.isSwitching = false;
				}, 400);
			}
		}
	},

	created() {
		// Initialize language on component creation
		if (this.$i18n && this.$i18n.locale) {
			this.$store.dispatch('lang/setLocale', { locale: this.$i18n.locale });
		}
	}
}
</script>

<style lang="scss" scoped>
.language-switcher {
	cursor: pointer;
	user-select: none;
}

.language-pill {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 18px;
	border-radius: 50px;

	// Hero-inspired styling
	background: rgba(255, 255, 255, 0.15);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	border: 2px solid rgba(255, 255, 255, 0.25);

	// Enhanced shadow
	box-shadow:
		0 4px 15px rgba(0, 0, 0, 0.2),
		inset 0 1px 0 rgba(255, 255, 255, 0.2);

	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

	&:hover {
		background: rgba(255, 255, 255, 0.25);
		border-color: rgba(255, 255, 255, 0.4);
		box-shadow:
			0 6px 20px rgba(0, 0, 0, 0.3),
			inset 0 1px 0 rgba(255, 255, 255, 0.3);
		transform: translateY(-2px);
	}

	&:active {
		transform: translateY(0);
		box-shadow:
			0 2px 10px rgba(0, 0, 0, 0.2),
			inset 0 1px 0 rgba(255, 255, 255, 0.2);
	}

	&.switching {
		animation: switchBounce 0.4s ease;
	}
}

.globe-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	color: #ffffff;
	transition: transform 0.3s ease;

	svg {
		width: 100%;
		height: 100%;
	}

	.language-pill:hover & {
		transform: rotate(15deg) scale(1.1);
	}
}

.lang-text {
	font-size: 15px;
	font-weight: 700;
	line-height: 1;
	letter-spacing: 0.5px;
	transition: all 0.3s ease;

	&.active {
		color: #fff200;
		text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
		font-size: 16px;
	}

	&.inactive {
		color: rgba(255, 255, 255, 0.7);
		font-size: 14px;
	}
}

.lang-divider {
	width: 2px;
	height: 18px;
	background: linear-gradient(
		to bottom,
		rgba(255, 255, 255, 0.1),
		rgba(255, 255, 255, 0.4),
		rgba(255, 255, 255, 0.1)
	);
	border-radius: 1px;
}

// Animation keyframes
@keyframes switchBounce {
	0% {
		transform: scale(1) rotate(0deg);
	}
	25% {
		transform: scale(0.95) rotate(-3deg);
	}
	50% {
		transform: scale(1.05) rotate(3deg);
	}
	75% {
		transform: scale(0.98) rotate(-1deg);
	}
	100% {
		transform: scale(1) rotate(0deg);
	}
}

// Responsive adjustments
@media (max-width: 768px) {
	.language-pill {
		padding: 8px 14px;
		gap: 8px;
	}

	.globe-icon {
		width: 20px;
		height: 20px;
	}

	.lang-text {
		font-size: 13px;

		&.active {
			font-size: 14px;
		}

		&.inactive {
			font-size: 12px;
		}
	}

	.lang-divider {
		height: 14px;
	}
}

@media (max-width: 480px) {
	.language-pill {
		padding: 7px 12px;
		gap: 6px;
	}

	.globe-icon {
		width: 18px;
		height: 18px;
	}

	.lang-text {
		font-size: 12px;

		&.active {
			font-size: 13px;
		}

		&.inactive {
			font-size: 11px;
		}
	}

	.lang-divider {
		height: 12px;
	}
}
</style>